/**
 * CompanyDocumentModal Component
 * 
 * Modal para upload e edição de documentos da empresa.
 * 
 * Funcionalidades:
 * - Formulário de upload/edição
 * - Seleção de categoria
 * - Campos de título, descrição, data de expiração
 * - Preview do arquivo
 * - Validação de formulário
 */

import { useState, useEffect } from 'react'
import Modal from '../ERP/Modal/Modal'
import DocumentUpload from './DocumentUpload'
import Button from '../Button/Button'
import './CompanyDocumentModal.css'

function CompanyDocumentModal({
  isOpen,
  onClose,
  onSubmit,
  document = null, // Para edição
  categories = [],
  loading = false
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [file, setFile] = useState(null)
  const [errors, setErrors] = useState({})

  const isEditMode = !!document

  useEffect(() => {
    if (isOpen) {
      if (document) {
        // Modo edição - preencher campos
        setTitle(document.title || '')
        setDescription(document.description || '')
        setCategoryId(document.category_id || '')
        setExpiryDate(document.expiry_date ? document.expiry_date.split('T')[0] : '')
        setFile(null) // Não precisamos do arquivo em edição
      } else {
        // Modo criação - limpar campos
        setTitle('')
        setDescription('')
        setCategoryId('')
        setExpiryDate('')
        setFile(null)
      }
      setErrors({})
    }
  }, [isOpen, document])

  const validate = () => {
    const newErrors = {}

    if (!title.trim()) {
      newErrors.title = 'Título é obrigatório'
    }

    if (!isEditMode && !file) {
      newErrors.file = 'Arquivo é obrigatório'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    const formData = {
      title: title.trim(),
      description: description.trim() || null,
      category_id: categoryId || null,
      expiry_date: expiryDate || null
    }

    if (isEditMode) {
      // Edição: apenas enviar dados (sem arquivo, a menos que tenha sido selecionado um novo)
      onSubmit({ ...formData, file: file || undefined }, document.id)
    } else {
      // Criação: enviar arquivo e dados
      onSubmit(formData, null, file)
    }
  }

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile)
    if (errors.file) {
      setErrors({ ...errors, file: null })
    }
  }

  const handleFileRemove = () => {
    setFile(null)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Editar Documento' : 'Adicionar Documento'}
      size="large"
    >
      <form className="company-document-modal-form" onSubmit={handleSubmit}>
        <div className="company-document-modal-body">
          {!isEditMode && (
            <div className="company-document-modal-field">
              <label className="company-document-modal-label">
                Arquivo <span className="required">*</span>
              </label>
              <DocumentUpload
                onFileSelect={handleFileSelect}
                onFileRemove={handleFileRemove}
                selectedFile={file}
                disabled={loading}
              />
              {errors.file && (
                <span className="company-document-modal-error">{errors.file}</span>
              )}
            </div>
          )}

          {isEditMode && document?.file_url && (
            <div className="company-document-modal-field">
              <label className="company-document-modal-label">Arquivo Atual</label>
              <div className="company-document-modal-file-preview">
                <a
                  href={document.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="company-document-modal-file-link"
                >
                  {document.file_name || 'Visualizar arquivo'} →
                </a>
              </div>
              <label className="company-document-modal-label">Substituir Arquivo (opcional)</label>
              <DocumentUpload
                onFileSelect={handleFileSelect}
                onFileRemove={handleFileRemove}
                selectedFile={file}
                disabled={loading}
              />
            </div>
          )}

          <div className="company-document-modal-field">
            <label className="company-document-modal-label" htmlFor="title">
              Título <span className="required">*</span>
            </label>
            <input
              id="title"
              type="text"
              className={`company-document-modal-input ${errors.title ? 'error' : ''}`}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value)
                if (errors.title) {
                  setErrors({ ...errors, title: null })
                }
              }}
              placeholder="Ex: Alvará de Funcionamento"
              disabled={loading}
            />
            {errors.title && (
              <span className="company-document-modal-error">{errors.title}</span>
            )}
          </div>

          <div className="company-document-modal-field">
            <label className="company-document-modal-label" htmlFor="description">
              Descrição
            </label>
            <textarea
              id="description"
              className="company-document-modal-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição opcional do documento..."
              rows={3}
              disabled={loading}
            />
          </div>

          <div className="company-document-modal-row">
            <div className="company-document-modal-field">
              <label className="company-document-modal-label" htmlFor="category">
                Categoria
              </label>
              <select
                id="category"
                className="company-document-modal-select"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                disabled={loading}
              >
                <option value="">Selecione uma categoria</option>
                {Array.isArray(categories) && categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="company-document-modal-field">
              <label className="company-document-modal-label" htmlFor="expiry_date">
                Data de Expiração
              </label>
              <input
                id="expiry_date"
                type="date"
                className="company-document-modal-input"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
        </div>

        <div className="company-document-modal-footer">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? 'Salvando...' : isEditMode ? 'Salvar Alterações' : 'Adicionar Documento'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default CompanyDocumentModal

